import {
  StylesFragment,
  type ZnglyContext_App_Query$data,
  type ZnglyContext_Styles_Fragment$key,
} from '@/graphql/queries/ZnglyContext.query';
import { useEffect, useMemo } from 'react';
import { useFragment } from 'react-relay';

const customStyle = document.createElement('style');
customStyle.setAttribute('nonce', window.znRuntime?.cspStrictValue || '');
document.head.append(customStyle);

const useStyleLoader = (queryRef: ZnglyContext_App_Query$data) => {
  const data = useFragment<ZnglyContext_Styles_Fragment$key>(StylesFragment, queryRef.znglyInfo);

  // load styles
  useEffect(() => {
    const znglyInfo = data?.znglyInfo ?? window?.znRuntime?.theme;
    if (!znglyInfo) return;

    customStyle.textContent = `
		:root {
			--font-style: ${znglyInfo.fontStyle};
			--page-p-text-col:  ${znglyInfo.pagePrimaryTxtClr};
			--page-s-text-col: ${znglyInfo.pageSecondaryTxtClr};
			--page-p-accent-col: ${znglyInfo.pagePrimaryAccClr};
			--page-s-accent-col: ${znglyInfo.pageSecondaryAccClr};
      --backend-p-col: ${znglyInfo.backendPrimaryColor};
      --backend-s-col: ${znglyInfo.backendSecondaryColor};
			--header-bg-col: ${znglyInfo.headerBgClr};
			--header-p-accent-col:  ${znglyInfo.headerPrimaryAccentClr};
			--header-s-accent-col: ${znglyInfo.headerSecondaryAccentClr};
			--header-p-text-col: ${znglyInfo.headerPrimaryTxtClr};
			--header-s-text-col: ${znglyInfo.headerSecondaryTxtClr};
			--user-menu-bg-col:  ${znglyInfo.userMenuBackgroundClr};
			--user-menu-link-col:  ${znglyInfo.userMenuLinkClr};
			--user-menu-link-hvr-col: ${znglyInfo.userMenuLinkHoverClr};
			--description-box-col: ${znglyInfo.bodyDescBoxClr};
			--description-box-text-col: ${znglyInfo.bodyDescBoxTxtClr};
			--footer-bg-col: ${znglyInfo.footerBgClr};
			--footer-accent-col: ${znglyInfo.footerAccentClr};
			--footer-p-text-col: ${znglyInfo.footerPrimaryTxtClr};
			--footer-s-text-col: ${znglyInfo.footerSecondaryTxtClr};
			--cat-znglist-col: ${znglyInfo.catZnglistClr};
			--dd-bg-col: ${znglyInfo.ddBgClr};
			--dd-p-txt-col: ${znglyInfo.ddPrimaryTxtClr};
			--dd-s-txt-col: ${znglyInfo.ddSecondaryTxtClr};
			--read-more-p-col: ${znglyInfo.readMorePrimaryClr};
			--read-more-s-col: ${znglyInfo.readMoreSecondaryClr};
			--read-more-p-txt-col: ${znglyInfo.readMorePrimaryTxtClr};
      --cta-p-col: ${znglyInfo.ctaPrimaryClr};
      --cta-s-col: ${znglyInfo.ctaSecondaryClr};
      --cta-p-txt-col: ${znglyInfo.ctaPrimaryTxtClr};
			--share-btn-p-col: ${znglyInfo.shareBtnPrimaryClr};
			--share-btn-s-col: ${znglyInfo.shareBtnSecondaryClr};
		}
		body {
			font-family: var(--font-style), 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans' !important;
		}
		`;
  }, [data?.znglyInfo]);

  const colorPresets = useMemo(() => {
    if (!data?.znglyInfo) return [];

    const colors = Object.values(data?.znglyInfo) as string[];

    // filter colors to only allow hex values
    const hexColors = colors.filter((color) => color.match(/^#([a-f0-9]{6}|[a-f0-9]{3})$/));

    const favColors: { [key: string]: number } = {};

    // count the most frequent colors
    hexColors.forEach((hexColor) => {
      if (favColors[hexColor]) {
        favColors[hexColor] += 1;
      } else {
        favColors[hexColor] = 1;
      }
    });

    // map the colors with the key value
    const sortableColors = Object.keys(favColors).map((key) => {
      return [key as string, favColors[key] as number];
    });

    // sort colors in order of frequency
    const sortedColors = sortableColors.sort((a, b) => {
      return (b[1] as number) - (a[1] as number);
    });

    // return only the color's hex value
    const presetColors: string[] = sortedColors.flatMap((color) => {
      return color[0] as string;
    });

    return presetColors;
  }, [data?.znglyInfo]);

  return { colorPresets };
};

export default useStyleLoader;
